import { useState, useEffect } from 'react';
import { X, Map } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { PropertyMap, MapLegend, MapDevice } from './PropertyMap';
import type { GPSLocation } from '../lib/googleMaps';

interface InspectionMapModalProps {
  jobSiteId: string;
  jobSiteName: string;
}

interface JobSiteRow {
  id: string;
  gps_location: GPSLocation | null;
}

interface ControllerRow {
  id: string;
  job_site_id: string | null;
  description: string | null;
  location: string | null;
  gps_location: GPSLocation | null;
}

interface ZoneRow {
  id: string;
  controller_id: string | null;
  zone_number: number;
  location: string | null;
  gps_location: GPSLocation | null;
}

interface WaterMeterRow {
  id: string;
  job_site_id: string | null;
  meter_number: string | null;
  location: string | null;
  gps_location: GPSLocation | null;
}

interface DoubleCheckRow {
  id: string;
  job_site_id: string | null;
  description: string | null;
  gps_location: GPSLocation | null;
}

export function InspectionMapButton({ jobSiteId, jobSiteName }: InspectionMapModalProps) {
  const { t } = useTranslation('inspection');
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-colors"
        title={t('map.viewPropertyMap')}
      >
        <Map size={14} />
        <span className="hidden sm:inline">{t('map.map')}</span>
      </button>

      {isOpen && (
        <InspectionMapModal
          jobSiteId={jobSiteId}
          jobSiteName={jobSiteName}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
}

interface ModalProps extends InspectionMapModalProps {
  onClose: () => void;
}

function InspectionMapModal({ jobSiteId, jobSiteName, onClose }: ModalProps) {
  const { t } = useTranslation('inspection');
  const [propertyLocation, setPropertyLocation] = useState<GPSLocation | null>(null);
  const [devices, setDevices] = useState<MapDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [noLocation, setNoLocation] = useState(false);

  useEffect(() => {
    loadMapData();
  }, [jobSiteId]);

  const loadMapData = async () => {
    setLoading(true);
    try {
      const site = await apiClient.get<JobSiteRow>(`/api/job-sites/${jobSiteId}`);

      if (!site?.gps_location?.latitude) {
        setNoLocation(true);
        setLoading(false);
        return;
      }

      setPropertyLocation(site.gps_location);

      const [allControllers, allMeters, allDoubleChecks, allZones] = await Promise.all([
        apiClient.get<ControllerRow[]>('/api/controllers'),
        apiClient.get<WaterMeterRow[]>('/api/water-meters'),
        apiClient.get<DoubleCheckRow[]>('/api/double-checks'),
        apiClient.get<ZoneRow[]>('/api/zones'),
      ]);

      const controllers = allControllers.filter(c => c.job_site_id === jobSiteId);
      const meters = allMeters.filter(m => m.job_site_id === jobSiteId);
      const doubleChecks = allDoubleChecks.filter(d => d.job_site_id === jobSiteId);
      const controllerIds = new Set(controllers.map(c => c.id));
      const zones = allZones.filter(z => z.controller_id && controllerIds.has(z.controller_id));

      const deviceList: MapDevice[] = [];

      controllers.forEach((c) => {
        if (c.gps_location?.latitude && c.gps_location?.longitude) {
          deviceList.push({
            id: c.id,
            type: 'controller',
            label: c.description || c.location || t('map.deviceController'),
            gps_location: c.gps_location,
          });
        }
      });

      zones.forEach((z) => {
        if (z.gps_location?.latitude && z.gps_location?.longitude) {
          deviceList.push({
            id: z.id,
            type: 'zone',
            label: `${t('map.deviceZone', { n: z.zone_number })}${z.location ? ` - ${z.location}` : ''}`,
            gps_location: z.gps_location,
          });
        }
      });

      meters.forEach((m) => {
        if (m.gps_location?.latitude && m.gps_location?.longitude) {
          deviceList.push({
            id: m.id,
            type: 'water_meter',
            label: m.meter_number ? t('map.deviceMeter', { n: m.meter_number }) : m.location || t('map.deviceWaterMeter'),
            gps_location: m.gps_location,
          });
        }
      });

      doubleChecks.forEach((d) => {
        if (d.gps_location?.latitude && d.gps_location?.longitude) {
          deviceList.push({
            id: d.id,
            type: 'double_check',
            label: d.description || t('map.deviceDoubleCheck'),
            gps_location: d.gps_location,
          });
        }
      });

      setDevices(deviceList);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-60"
        onClick={onClose}
      />

      <div className="relative w-full sm:max-w-3xl sm:mx-4 sm:rounded-xl overflow-hidden shadow-2xl flex flex-col bg-surface h-full sm:h-[75vh]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-line bg-surface flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-theme-primary">{jobSiteName}</h2>
            <p className="text-xs text-theme-secondary mt-0.5">
              {t('map.devicesWithGps', { count: devices.length })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-subtle rounded-lg transition-colors text-theme-secondary"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64 text-theme-secondary text-sm gap-2">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              {t('map.loading')}
            </div>
          ) : noLocation ? (
            <div className="flex flex-col items-center justify-center h-64 text-center px-6">
              <div className="w-12 h-12 bg-subtle rounded-full flex items-center justify-center mb-3">
                <Map size={22} className="text-theme-secondary" />
              </div>
              <p className="text-theme-primary font-medium text-sm">{t('map.noGpsTitle')}</p>
              <p className="text-theme-secondary text-xs mt-1">
                {t('map.noGpsHint')}
              </p>
            </div>
          ) : propertyLocation ? (
            <div className="flex flex-col h-full">
              <div className="flex-1 min-h-0">
                <PropertyMap
                  propertyLocation={propertyLocation}
                  propertyName={jobSiteName}
                  devices={devices}
                  height="100%"
                />
              </div>
              <div className="px-4 py-2 border-t border-line bg-surface flex-shrink-0">
                <MapLegend devices={devices} />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
